import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BarChart3, Mail, Lock, Loader2, KeyRound, ArrowLeft, CheckCircle } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import "@/styles/fiori.css";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Password reset modal state
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code, 3: new password
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetUsername, setResetUsername] = useState("");
  const [resetting, setResetting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      login(userData, access_token);
      toast.success(`${t('welcome')}, ${userData.username}!`);
      navigate(userData.role === "admin" ? "/admin" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Check if email exists
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error(t('fillAllFields'));
      return;
    }

    setResetting(true);
    try {
      // First check if email exists
      const checkResponse = await axios.post(`${API}/auth/check-email`, { email: resetEmail });
      setResetUsername(checkResponse.data.username || "");
      
      // Send verification code
      await axios.post(`${API}/auth/send-reset-code`, { email: resetEmail });
      toast.success(t('codeSent'));
      setResetStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('userNotFound'));
    } finally {
      setResetting(false);
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!resetCode) {
      toast.error(t('fillAllFields'));
      return;
    }

    setResetting(true);
    try {
      await axios.post(`${API}/auth/verify-reset-code`, { 
        email: resetEmail, 
        code: resetCode 
      });
      toast.success(t('codeVerified'));
      setResetStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalidCode'));
    } finally {
      setResetting(false);
    }
  };

  // Step 3: Set new password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPassword || !resetConfirmPassword) {
      toast.error(t('fillAllFields'));
      return;
    }
    if (resetPassword !== resetConfirmPassword) {
      toast.error(t('passwordsDontMatch'));
      return;
    }
    if (resetPassword.length < 4) {
      toast.error(t('passwordMinLength'));
      return;
    }

    setResetting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: resetPassword
      });
      toast.success(t('resetPasswordSuccess'));
      closeResetModal();
      // Pre-fill login form
      setEmail(resetEmail);
      setPassword(resetPassword);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdating'));
    } finally {
      setResetting(false);
    }
  };

  const closeResetModal = () => {
    setResetModalOpen(false);
    setResetStep(1);
    setResetEmail("");
    setResetCode("");
    setResetPassword("");
    setResetConfirmPassword("");
    setResetUsername("");
  };

  const openResetModal = () => {
    setResetStep(1);
    setResetModalOpen(true);
  };

  return (
    <div className="fiori-login-page">
      {/* Language selector */}
      <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
        <LanguageSelector />
      </div>

      <div className="fiori-login-card">
        {/* Logo */}
        <div className="fiori-login-logo">
          <div className="fiori-login-logo-icon">
            <BarChart3 size={28} />
          </div>
          <div>
            <div className="fiori-login-title">{t('appName')}</div>
            <div className="fiori-login-subtitle">{t('appSubtitle')}</div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="fiori-form-group">
            <label className="fiori-form-label">{t('contactEmail')}</label>
            <div style={{ position: 'relative' }}>
              <Mail size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--sapContent_LabelColor)' }} />
              <input
                type="email"
                className="fiori-form-input"
                style={{ paddingLeft: '2.5rem' }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('enterEmail')}
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="fiori-form-group">
            <label className="fiori-form-label">{t('password')}</label>
            <div style={{ position: 'relative' }}>
              <Lock size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--sapContent_LabelColor)' }} />
              <input
                type="password"
                className="fiori-form-input"
                style={{ paddingLeft: '2.5rem' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('enterPassword')}
                data-testid="login-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            className="fiori-button emphasized"
            style={{ width: '100%', justifyContent: 'center', marginTop: '1.5rem' }}
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {t('login')}
          </button>
        </form>

        {/* Forgot password link */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={openResetModal}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--sapBrandColor)',
              cursor: 'pointer',
              fontSize: '0.875rem',
              textDecoration: 'underline'
            }}
            data-testid="forgot-password-link"
          >
            {t('forgotPassword')}
          </button>
        </div>
      </div>

      {/* Password Reset Modal - Multi-step */}
      <Dialog open={resetModalOpen} onOpenChange={(open) => !open && closeResetModal()}>
        <DialogContent className="fiori-dialog sm:max-w-md">
          <DialogHeader className="fiori-dialog-header">
            <DialogTitle className="fiori-dialog-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <KeyRound size={20} />
              {t('resetPassword')}
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--sapContent_LabelColor)' }}>
              {resetStep === 1 && t('resetStep1Desc')}
              {resetStep === 2 && t('resetStep2Desc')}
              {resetStep === 3 && t('resetStep3Desc')}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', padding: '0.5rem 0' }}>
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                style={{
                  width: '2rem',
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: resetStep >= step ? 'var(--sapBrandColor)' : 'var(--sapContent_LabelColor)',
                  opacity: resetStep >= step ? 1 : 0.3,
                  transition: 'all 0.3s'
                }}
              />
            ))}
          </div>

          <div className="fiori-dialog-content">
            {/* Step 1: Email */}
            {resetStep === 1 && (
              <form onSubmit={handleCheckEmail}>
                <div className="fiori-form-group">
                  <label className="fiori-form-label">{t('contactEmail')} *</label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--sapContent_LabelColor)' }} />
                    <input
                      type="email"
                      className="fiori-form-input"
                      style={{ paddingLeft: '2.5rem' }}
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder={t('enterEmail')}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="fiori-dialog-footer" style={{ padding: 0, border: 'none', marginTop: '1rem' }}>
                  <button type="button" className="fiori-button" onClick={closeResetModal}>
                    {t('cancel')}
                  </button>
                  <button type="submit" className="fiori-button emphasized" disabled={resetting}>
                    {resetting ? <Loader2 size={14} className="animate-spin" /> : null}
                    {t('sendCode')}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Verification Code */}
            {resetStep === 2 && (
              <form onSubmit={handleVerifyCode}>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <p style={{ color: 'var(--sapTextColor)', fontSize: '0.875rem' }}>
                    {t('codeSentTo')} <strong>{resetEmail}</strong>
                  </p>
                  {resetUsername && (
                    <p style={{ color: 'var(--sapContent_LabelColor)', fontSize: '0.8125rem' }}>
                      {t('user')}: {resetUsername}
                    </p>
                  )}
                </div>
                <div className="fiori-form-group">
                  <label className="fiori-form-label">{t('verificationCode')} *</label>
                  <input
                    type="text"
                    className="fiori-form-input"
                    style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem', fontFamily: 'monospace' }}
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                  />
                </div>
                <div className="fiori-dialog-footer" style={{ padding: 0, border: 'none', marginTop: '1rem' }}>
                  <button type="button" className="fiori-button" onClick={() => setResetStep(1)}>
                    <ArrowLeft size={14} /> {t('back')}
                  </button>
                  <button type="submit" className="fiori-button emphasized" disabled={resetting || resetCode.length !== 6}>
                    {resetting ? <Loader2 size={14} className="animate-spin" /> : null}
                    {t('verify')}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: New Password */}
            {resetStep === 3 && (
              <form onSubmit={handleResetPassword}>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <CheckCircle size={32} style={{ color: 'var(--sapPositiveColor)' }} />
                  <p style={{ color: 'var(--sapPositiveColor)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    {t('codeVerified')}
                  </p>
                </div>
                <div className="fiori-form-group">
                  <label className="fiori-form-label">{t('newPassword')} *</label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--sapContent_LabelColor)' }} />
                    <input
                      type="password"
                      className="fiori-form-input"
                      style={{ paddingLeft: '2.5rem' }}
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder={t('enterPassword')}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="fiori-form-group">
                  <label className="fiori-form-label">{t('confirmPassword')} *</label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--sapContent_LabelColor)' }} />
                    <input
                      type="password"
                      className="fiori-form-input"
                      style={{ paddingLeft: '2.5rem' }}
                      value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      placeholder={t('confirmPassword')}
                    />
                  </div>
                </div>
                <div className="fiori-dialog-footer" style={{ padding: 0, border: 'none', marginTop: '1rem' }}>
                  <button type="button" className="fiori-button" onClick={closeResetModal}>
                    {t('cancel')}
                  </button>
                  <button type="submit" className="fiori-button emphasized" disabled={resetting}>
                    {resetting ? <Loader2 size={14} className="animate-spin" /> : null}
                    {t('resetPassword')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
